import {
  ArrayOrSingle,
  FilterKeysOfType,
  Merge,
} from '@shared/models/type-helpers.model';
import { replaceOrAddIdItem } from '@shared/helpers/array.helpers';
import {
  ChildrenKey,
  SortingOption,
  flattenTree,
  getPathInFlatTree,
  getTreeItemChildren,
} from '@shared/helpers/tree.helpers';
import { searchFilter } from '@shared/helpers/string.helpers';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { SetStateAction } from 'react';
import { v4 } from 'uuid';
import { useSharedMemo } from './use-utils.hooks';
import { compare } from '@shared/helpers/compare.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';

const DEBUG = false;

interface IFilterOptionBase {
  id: string;
  disabled?: boolean;
}

/**
 * Finter function that takes a value and a path (all parents to the elements) and returns a boolean
 */
export type FilterFn<T> = (value: T, path: T[]) => boolean | undefined;

/**
 * Function filter
 */
interface IFilterFunctionOption<T extends object> extends IFilterOptionBase {
  function: FilterFn<T>;
}

/**
 * Search filter
 */
interface IFilterSearchOption<T extends object> extends IFilterOptionBase {
  /**
   * Keys to search in. Must have string values
   */
  searchIn: FilterKeysOfType<Merge<T>, string | undefined>[];

  /**
   * Current search query. Undefined or empty string will not filter.
   * Space separated words will be treated as an AND search.
   */
  query?: string;
}

type CompareFn<T extends object> = (a: T, b: T) => boolean | undefined;

/**
 * Options for {@link getSortFilteredChildren}.
 */
export type SortFilteredChildrenOptions<T extends object> = {
  childrenKey?: ChildrenKey<T>;
  sorting?: SortingOption<T>[];
  compareFn: CompareFn<T>;
};

/**
 * Pick the filtered, sorted children of {@link parent}, or the filtered roots when it is omitted.
 * Items whose parent is missing from {@link filteredItems} surface as roots (search bubbling).
 */
export const getSortFilteredChildren = <T extends object>(
  filteredItems: T[],
  parent: T | undefined,
  { childrenKey, sorting, compareFn }: SortFilteredChildrenOptions<T>,
): T[] => {
  if (!parent) {
    return filteredItems.filter(
      (item) =>
        !filteredItems.some((candidateParent) =>
          getTreeItemChildren(candidateParent, childrenKey, sorting).some((c) =>
            compareFn(item, c),
          ),
        ),
    );
  }

  const children = getTreeItemChildren(parent, childrenKey, sorting);
  return filteredItems.filter((el) => children.some((c) => compareFn(el, c)));
};

/**
 * Different types of filter options
 */
type IFilterOption<T extends object> =
  IFilterFunctionOption<T> | IFilterSearchOption<T>;

/**
 * Input options for the filter store
 */
interface IFilterStoreOptions<T extends object, K = ChildrenKey<T>> {
  childrenKey?: K;
  filters?: IFilterOption<T>[];
  sorting?: SortingOption<T>[];
  initialValues?: ArrayOrSingle<T | undefined>;
  name?: string;
  /**
   * To test if two items are equal.
   * This is used by filters and sorting to match elements.
   * Will use strict equals if not provided.
   * @param a
   * @param b
   * @returns
   */
  compareFn?: CompareFn<T>;
}

/**
 * Return type of the filter store
 */
export interface IFilterStore<T extends object> {
  /**
   * Overwrite sorting with a new array of sorting options
   */
  setSorting: (sorting?: SortingOption<T>[]) => void;

  /**
   * Set a single sorting option. If an option with the same id already exists, it will be replaced.
   */
  setSortingOption: (option: SortingOption<T>) => void;

  /**
   * Overwrite filters with a new array of filters
   */
  setFilters: (filters?: IFilterOption<T>[]) => void;

  /**
   * Add a new filter. If a filter with the same id already exists, it will be replaced.
   */
  setFilter: (newFilter: IFilterOption<T>) => void;

  /**
   * Overwrite the compare function
   */
  setCompareFn: (compareFn?: CompareFn<T>) => void;

  /**
   * Filter items and sort them
   */
  useSortFilteredItems: (rootItems: T[]) => T[];

  /**
   * Get all filtered children of an item
   */
  useSortFilteredChildren: (rootItems: T[], item?: T) => T[];
}

/**
 * Create a new filter store that can sort and filter a tree or array of items
 */
export const createSortFilterStore = <T extends object>({
  name = v4(),
  childrenKey,
  filters = [],
  sorting = [],
  compareFn = compare.equal.strict,
}: IFilterStoreOptions<T> = {}): IFilterStore<T> => {
  const useSortFilterStore = createWithEqualityFn<{
    filters: IFilterOption<T>[];
    sorting: SortingOption<T>[];
  }>()(() => ({ filters, sorting }), shallow);

  const { setState } = useSortFilterStore;

  const setSorting = (sorting: SortingOption<T>[] = []) => {
    setState((prev) => {
      return compare.equal.shallow(prev.sorting, sorting) ? prev : { sorting };
    });
  };

  const setSortingOption = (option: SortingOption<T>) =>
    setState((prev) => ({
      sorting: replaceOrAddIdItem(prev.sorting, option),
    }));

  // Change filters
  const setFilters = (filters: SetStateAction<IFilterOption<T>[]> = []) => {
    setState((prev) => {
      const nextFilters = guards.is.array(filters)
        ? filters
        : filters(prev.filters);
      return compare.equal.shallow(prev.filters, nextFilters)
        ? prev // ensure object reference is the same when filters are shallow equal
        : { filters: nextFilters };
    });
  };

  // Set a single filter (replace if it already exists)
  const setFilter = (newFilter: IFilterOption<T>) =>
    setFilters((prevFilters) => replaceOrAddIdItem(prevFilters, newFilter));

  // Set the compare function
  const setCompareFn = (fn: CompareFn<T> = compare.equal.strict) => {
    compareFn = fn;
  };

  /** Flattened array containing only the items that match the filters (in order of appearance) */
  const useSortFilteredItems = (rootItems: T[]): T[] => {
    const { sorting, filters } = useSortFilterStore();

    return useSharedMemo(
      () => {
        const flat = flattenTree(rootItems, childrenKey, sorting);
        return filterItems(flat, childrenKey, filters);
      },
      `useSortFilterItems[${name}]`,
      [rootItems, childrenKey, sorting, filters],
    );
  };

  const useSortFilteredChildren = (rootItems: T[], parent?: T) => {
    const filteredItems = useSortFilteredItems(rootItems);
    const parentKey =
      parent && 'id' in parent && parent.id != null
        ? String(parent.id)
        : parent
          ? 'no-id'
          : 'root';

    return useSharedMemo(
      () =>
        getSortFilteredChildren(filteredItems, parent, {
          childrenKey,
          sorting,
          compareFn,
        }),
      `useSortFilteredChildren[${name}]:${parentKey}`,
      [filteredItems, parent, compareFn, sorting],
    );
  };

  return {
    setSorting,
    setSortingOption,
    setFilters,
    setFilter,
    setCompareFn,
    useSortFilteredItems,
    useSortFilteredChildren,
  };
};

const filterDebug = <T extends object>(
  response: boolean | undefined,
  item: T,
  filter: IFilterOption<T>,
): boolean | undefined => {
  if (DEBUG && !response) {
    console.info(`Item filtered by "${filter.id}"`, item, filter);
  }
  return response;
};

/**
 * Handle filtering of items
 * @param flatTree Already flattened tree
 * @param childrenKey Key to children
 * @param filters Filters to apply
 * @returns
 */
const filterItems = <T extends object>(
  flatTree: T[],
  childrenKey: ChildrenKey<T> | undefined,
  filters: IFilterOption<T>[],
): T[] => {
  const activeFilters = filters.filter((filter) => !filter.disabled);
  // No filters => show everything
  if (!activeFilters.length) {
    return flatTree;
  }

  // Filter items
  const filtered = flatTree.filter((item) => {
    const path = getPathInFlatTree(
      flatTree,
      item,
      // TS 6 no longer matches ChildrenKey<T> against the instantiated parameter type (cf. cast in tree.helpers.ts)
      childrenKey as Parameters<typeof getPathInFlatTree<T>>[2],
    );
    return activeFilters.every((filter) => {
      const fn = 'function' in filter && filter.function;
      const searchIn = 'searchIn' in filter && filter.searchIn;
      // Search filter
      if (searchIn) {
        const searchData = searchIn.map((key) => item[key] ?? '').join(' ');
        return filterDebug(
          searchFilter(searchData, filter.query),
          item,
          filter,
        );
      }
      // Function filter (functions that returns a boolean)
      if (fn) {
        return filterDebug(fn(item, path), item, filter);
      }
      return true;
    });
  });
  return filtered;
};
